import { RefObject, useEffect, useRef } from "react";

import type { EditorView } from "prosemirror-view";

import { SpellChecker, getSpellChecker } from "./dictionary";
import { tokenizeWords } from "./tokenize";

/**
 * App-side spell-check overlay for the Report Builder editor.
 *
 * The vendored docx-editor renders ProseMirror off-screen and paints the visible
 * pages as a separate *contenteditable* DOM projection, so PM decorations and
 * native browser spell-check never reach the visible text. We therefore check the
 * painted text ourselves (on-device en-AU `nspell`, [[dictionary]] — no report
 * text leaves the browser) and mark misspellings.
 *
 * MDRS-402: we used to mark them by wrapping each misspelled word in a squiggle
 * `<span>` *inside* the painted paragraph. But that projection is the fork's
 * contenteditable, and injecting elements into it desynced the fork's DOM↔caret
 * mapping — the caret got trapped at a marked word, and with a broken selection
 * the toolbar (Bold/Italic need a selection) and the click-to-fix both stopped
 * working. So we now NEVER mutate the editor's DOM: we read the words + their
 * ProseMirror ranges (`findMisspelledWords`) and draw the squiggles in a separate,
 * non-interactive overlay layer positioned over the page. Clicks are hit-tested
 * against the flagged words via the browser's own `caretRangeFromPoint`
 * (`matchWordAt`) without preventing the editor's normal caret placement.
 *
 * The painted paragraph fragments carry `data-pm-start` (the paragraph's PM node
 * position), so a word at character offset `n` within the paragraph maps to PM
 * range `pmStart + 1 + n` (the Refine feature's convention). The click-to-fix
 * still verifies the range holds the word before editing (drift guard).
 */
const SQUIGGLE_CLASS = "spellcheck-squiggle";
const LAYER_CLASS = "spellcheck-layer";
// A small repeating red wave, painted along the bottom edge of each word rect.
const SQUIGGLE_BG =
  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='6' height='3'><path d='M0 2 C 1.5 2 1.5 0.5 3 0.5 S 4.5 2 6 2' stroke='%23d64545' fill='none' stroke-width='0.9'/></svg>\")";

/**
 * A misspelled word located in the painted DOM. Carries both the DOM position
 * (`node` + local `start`/`end`, for drawing the squiggle and hit-testing a
 * click) and the ProseMirror range (`from`/`to`, for applying a correction).
 * `from`/`to` are `NaN` when the containing fragment has no `data-pm-start`.
 */
export type MisspelledWord = {
  node: Text;
  /** Character offset of the word within `node`. */
  start: number;
  end: number;
  word: string;
  /** ProseMirror position of the word start (`pmStart + 1 + paragraph offset`). */
  from: number;
  to: number;
};

/**
 * Painted decorations the layout painter injects into a paragraph: the list
 * marker (`1.`, `•`) that opens a list item, and the `¶` glyph on a paragraph
 * whose mark is a pending tracked change.
 *
 * Both are real text nodes inside the painted paragraph, but neither has any
 * ProseMirror content behind it — the marker is derived from the numbering
 * definition, not from the paragraph's text. Counting them shifts every
 * following word's computed PM position, the click-to-fix drift guard then
 * reads the wrong text, and the correction is silently declined (MDRS-513).
 *
 * The mirror image of a decoration is painted content that costs FEWER
 * characters than ProseMirror positions — see `brWidth` below for the hard
 * break, which is handled.
 *
 * One imbalance remains and can't be closed by exclusion: a dot-leader tab
 * paints ~1000 leader characters inside `.layout-run-tab`, but ProseMirror
 * holds a single `tab` leaf (one position). Dropping it would under-count by
 * one just as badly as counting it over-counts, so a leader tab needs to be
 * counted as exactly ONE character — the same shape of fix as `brWidth`, but
 * it has to *replace* a character count rather than add one. Words after a
 * leader tab therefore still drift.
 */
const DECORATION_SELECTOR = ".layout-list-marker, .layout-revision-pmark-glyph";

/** The painter's class for a paragraph fragment — the ONE painted element whose
 *  `data-pm-start` matches the offsets `findMisspelledWords` computes. */
export const PARAGRAPH_FRAGMENT_CLASS = "layout-paragraph";

/** True when `text` sits inside a painter decoration within `root`. Walks up to
 *  `root` rather than using `closest`, so a decoration *above* the paragraph
 *  can never swallow the whole fragment. */
function inDecoration(text: Node, root: Element): boolean {
  let el = text.parentElement;
  while (el && el !== root) {
    if (el.matches(DECORATION_SELECTOR)) return true;
    el = el.parentElement;
  }
  return false;
}

/**
 * How many characters a `<br>` contributes to the running offset.
 *
 * A Shift+Enter is a `hardBreak`, which occupies exactly ONE ProseMirror
 * position, and the painter renders it as a bare `<br>` carrying no text. Left
 * uncounted, every word after a line break resolved one position early and
 * click-to-fix silently declined — the same symptom as the list-marker bug, in
 * the commonest editing gesture after Enter.
 *
 * It is not always 1, which is why this can't be a constant. When a line's ONLY
 * run is the break (consecutive `<w:br/>`, i.e. a blank row) the painter also
 * prepends a zero-width `\u200B` span carrying the SAME pm position, so the
 * caret resolvers can find it. That marker is a real text node and already
 * contributes its one character — counting the `<br>` too would over-count by
 * one and break the line the fix is meant to repair.
 */
function brWidth(br: Element): number {
  const pmStart = (br as HTMLElement).dataset.pmStart;
  if (pmStart === undefined) return 0;
  // The zero-width marker, when present, is a sibling carrying the same
  // position. If it exists it has already paid for this break.
  const marker = br.parentElement?.querySelector(
    `span[data-pm-start="${CSS.escape(pmStart)}"]`
  );
  return marker ? 0 : 1;
}

/**
 * A walker over a painted paragraph that yields the content ProseMirror also
 * has, in the same order: text nodes, plus the `<br>` elements that occupy a PM
 * position without painting text. Decorations are rejected — rejecting an
 * element prunes its subtree, so a decoration's text never reaches the caller.
 */
function contentWalker(root: Element): TreeWalker {
  return document.createTreeWalker(
    root,
    NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT,
    {
      acceptNode: (node) => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          const el = node as Element;
          if (el.matches(DECORATION_SELECTOR)) return NodeFilter.FILTER_REJECT;
          // Other elements are containers: skip the node itself, keep walking
          // into it. `<br>` is surfaced so the caller can charge it a position.
          return el.tagName === "BR"
            ? NodeFilter.FILTER_ACCEPT
            : NodeFilter.FILTER_SKIP;
        }
        return inDecoration(node, root)
          ? NodeFilter.FILTER_REJECT
          : NodeFilter.FILTER_ACCEPT;
      },
    }
  );
}

/** Characters `node` contributes to the paragraph offset. */
function contentWidth(node: Node): number {
  if (node.nodeType === Node.ELEMENT_NODE) return brWidth(node as Element);
  return (node.nodeValue ?? "").length;
}

/**
 * Character offset of `target`'s text within its containing paragraph element —
 * the sum of all text before it. Maps to the PM content offset for plain text.
 * Exported for testing.
 */
export function offsetWithinParagraph(paraEl: Element, target: Node): number {
  const walker = contentWalker(paraEl);
  let offset = 0;
  let node = walker.nextNode();
  while (node) {
    if (node === target || target.contains(node)) break;
    offset += contentWidth(node);
    node = walker.nextNode();
  }
  return offset;
}

/**
 * Read the misspelled words in a painted paragraph fragment WITHOUT mutating it.
 * Walks the fragment's text nodes, tokenises each, drops tokens inside `<< … >>`
 * merge placeholders, and maps each misspelling to its ProseMirror range using the
 * running character offset within the paragraph. Exported for testing.
 */
export function findMisspelledWords(
  paraEl: HTMLElement,
  isCorrect: (word: string) => boolean
): MisspelledWord[] {
  const pmStart = Number(paraEl.dataset.pmStart);
  const hasPm = !Number.isNaN(pmStart);
  const out: MisspelledWord[] = [];
  const walker = contentWalker(paraEl);
  // Running count of characters before the current node, so a word's paragraph
  // offset = paragraphOffsetOfNode + wordOffsetWithinNode.
  let paragraphOffset = 0;
  let node = walker.nextNode();
  while (node) {
    if (node.nodeType === Node.ELEMENT_NODE) {
      // A `<br>` paints nothing but still costs a PM position.
      paragraphOffset += brWidth(node as Element);
      node = walker.nextNode();
      continue;
    }
    const textNode = node as Text;
    const text = textNode.nodeValue ?? "";
    // Template merge fields (e.g. <<Client_SalLastName>>) aren't real words —
    // exclude any token inside a << … >> placeholder.
    const mergeRanges: Array<[number, number]> = [];
    for (const m of text.matchAll(/<<[^>]*>>/g)) {
      const s = m.index ?? 0;
      mergeRanges.push([s, s + m[0].length]);
    }
    const inMergeField = (start: number, end: number) =>
      mergeRanges.some(([s, e]) => start < e && end > s);

    for (const t of tokenizeWords(text)) {
      if (inMergeField(t.start, t.end) || isCorrect(t.word)) continue;
      const from = hasPm ? pmStart + 1 + paragraphOffset + t.start : NaN;
      out.push({
        node: textNode,
        start: t.start,
        end: t.end,
        word: t.word,
        from,
        to: hasPm ? from + t.word.length : NaN,
      });
    }
    paragraphOffset += text.length;
    node = walker.nextNode();
  }
  return out;
}

/**
 * Collect every misspelled word across a painted container.
 *
 * Scoped to painted PARAGRAPH fragments — never the hidden off-screen
 * ProseMirror DOM, and never the other painted elements that also carry
 * `data-pm-start`: the painter puts it on tables, table cells and every run span
 * too. An unscoped `[data-pm-start]` yields the same word several times over,
 * and for text inside a table the TABLE's entry comes first in document order —
 * so `matchWordAt` returned a range computed from the table's position, the
 * drift guard saw the wrong text, and the correction was declined. Click-to-fix
 * was silently dead inside tables for the same reason it was inside lists
 * (MDRS-513).
 *
 * This exists as its own function so the scoping is reachable from a test. The
 * first version of that test rebuilt the query itself and passed against the
 * unscoped selector, guarding nothing.
 */
export function collectMisspellings(
  container: Element,
  isCorrect: (word: string) => boolean
): MisspelledWord[] {
  const out: MisspelledWord[] = [];
  container
    .querySelectorAll<HTMLElement>(`.${PARAGRAPH_FRAGMENT_CLASS}[data-pm-start]`)
    .forEach((frag) => {
      out.push(...findMisspelledWords(frag, isCorrect));
    });
  return out;
}

/**
 * Find the flagged word a click landed on, given the text node + character offset
 * the browser reported for the click point (via `caretRangeFromPoint`). Returns
 * the word whose range straddles the offset, or null. Exported for testing.
 */
export function matchWordAt(
  words: MisspelledWord[],
  node: Node | null,
  offset: number
): MisspelledWord | null {
  if (!node) return null;
  return (
    words.find((w) => w.node === node && offset >= w.start && offset <= w.end) ??
    null
  );
}

/** The text node + character offset under a viewport point, or null. */
function caretNodeOffsetAtPoint(
  x: number,
  y: number
): { node: Node; offset: number } | null {
  type CaretDoc = Document & {
    caretRangeFromPoint?: (x: number, y: number) => Range | null;
    caretPositionFromPoint?: (
      x: number,
      y: number
    ) => { offsetNode: Node; offset: number } | null;
  };
  const doc = document as CaretDoc;
  if (typeof doc.caretRangeFromPoint === "function") {
    const range = doc.caretRangeFromPoint(x, y);
    if (range) return { node: range.startContainer, offset: range.startOffset };
  }
  if (typeof doc.caretPositionFromPoint === "function") {
    const pos = doc.caretPositionFromPoint(x, y);
    if (pos) return { node: pos.offsetNode, offset: pos.offset };
  }
  return null;
}

/**
 * True when every mutation in the batch targets `el` or a descendant of it —
 * i.e. the whole batch is our own squiggle-layer redraw, not a real editor edit.
 *
 * The squiggle layer lives *inside* the observed `container` (it's attached to
 * the paginated viewport), so `redraw()`'s `replaceChildren()` / `appendChild()`
 * are seen by the MutationObserver. Without this filter each redraw would
 * reschedule a rescan → redraw → rescan **300ms loop** for as long as the doc
 * has any misspelling (MDRS-402 review). Exported for testing.
 */
export function mutationsConfinedTo(
  records: ReadonlyArray<Pick<MutationRecord, "target">>,
  el: Node
): boolean {
  return records.length > 0 && records.every((r) => el.contains(r.target));
}

export function useSpellcheckOverlay(
  containerRef: RefObject<HTMLElement | null>,
  viewRef: RefObject<EditorView | null>,
  /** Called after a correction is applied, so the host can arm autosave — the
   *  overlay dispatches its fix straight to the PM view, which doesn't reliably
   *  reach the editor's own onChange (MDRS-405). Held in a ref so changing it
   *  never re-runs the effect. */
  onEdit?: () => void
): void {
  const onEditRef = useRef(onEdit);
  onEditRef.current = onEdit;
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let checker: SpellChecker | null = null;
    let observer: MutationObserver | null = null;
    let timer: number | undefined;
    let disposed = false;
    // Latest scan — the click handler hit-tests against this.
    let words: MisspelledWord[] = [];

    // Non-interactive squiggle layer. The fork scrolls the pages in an ancestor
    // AND scales them with a CSS `transform: scale(zoom)` on a "viewport" div
    // (the parent of `.paged-editor__pages`). To co-move with BOTH — natively,
    // no per-frame JS — the layer is attached INSIDE that transformed viewport
    // and positioned in its LOCAL (unscaled) coordinate space: scrolling moves
    // the viewport, zooming scales it, and the layer (a child) goes along with
    // both, staying glued to the text. It still never touches the fork's
    // contenteditable text (the MDRS-402 caret fix): it's an out-of-flow,
    // pointer-events:none sibling of the pages.
    const layer = document.createElement("div");
    layer.className = LAYER_CLASS;
    layer.setAttribute("aria-hidden", "true");
    layer.style.cssText =
      "position:absolute;top:0;left:0;width:0;height:0;pointer-events:none;z-index:5;";

    let host: HTMLElement | null = null;
    let setHostRelative = false;
    const findHost = (): HTMLElement => {
      // The transformed viewport is the parent of `.paged-editor__pages`.
      const pages = container.querySelector<HTMLElement>(".paged-editor__pages");
      const viewport = pages?.parentElement ?? null;
      if (viewport) return viewport;
      // Fallback: nearest scrollable ancestor of a painted paragraph.
      let node: HTMLElement | null =
        container.querySelector<HTMLElement>("[data-pm-start]");
      while (node && node !== container.parentElement) {
        const oy = getComputedStyle(node).overflowY;
        if (
          (oy === "auto" || oy === "scroll" || oy === "overlay") &&
          node.scrollHeight > node.clientHeight + 1
        ) {
          return node;
        }
        node = node.parentElement;
      }
      return container;
    };
    const ensureLayerAttached = () => {
      const h = findHost();
      if (h !== host) {
        host = h;
        // The layer positions in the host's local space, so the host must be its
        // containing block. At zoom≠1 the host already is one (it has a
        // transform); at zoom=1 there's no transform, so nudge a static host to
        // relative (benign, out-of-flow, reverted on cleanup) — never the text.
        if (getComputedStyle(h).position === "static") {
          h.style.position = "relative";
          setHostRelative = true;
        }
      }
      if (host && layer.parentElement !== host) host.appendChild(layer);
    };

    const drawSquiggle = (hostRect: DOMRect, scale: number, rect: DOMRect) => {
      if (rect.width === 0 || rect.height === 0) return;
      const sq = document.createElement("div");
      sq.className = SQUIGGLE_CLASS;
      // Convert the word's on-screen rect into the host's LOCAL (pre-transform)
      // space: subtract the host origin and divide out the current scale. Because
      // the layer shares the host's transform, these local coords stay aligned
      // with the text at every zoom and scroll offset — no recompute needed on
      // either. The underline (3px local) scales with the text, like the text's
      // own decoration would.
      const left = (rect.left - hostRect.left) / scale;
      const top = (rect.top - hostRect.top) / scale;
      const width = rect.width / scale;
      const height = rect.height / scale;
      sq.style.cssText = [
        "position:absolute",
        `left:${left}px`,
        `top:${top + height - 3}px`,
        `width:${width}px`,
        "height:3px",
        "pointer-events:none",
        `background-image:${SQUIGGLE_BG}`,
        "background-repeat:repeat-x",
        "background-position:left bottom",
      ].join(";");
      layer.appendChild(sq);
    };

    // Redraw squiggles for the current `words` (no dictionary work). Local-space
    // coordinates mean this does NOT need to run on scroll OR zoom — the browser
    // moves and scales the layer with the pages. It runs only when the words or
    // the document layout change.
    const redraw = () => {
      if (disposed) return;
      ensureLayerAttached();
      const h = host;
      if (!h) return;
      const hostRect = h.getBoundingClientRect();
      // Current scale = rendered width / layout width (1 when not zoomed).
      const scale = h.offsetWidth > 0 ? hostRect.width / h.offsetWidth : 1;
      layer.replaceChildren();
      for (const w of words) {
        const range = document.createRange();
        try {
          range.setStart(w.node, w.start);
          range.setEnd(w.node, w.end);
        } catch {
          continue; // node/offset drifted (fork repainted) — next rescan fixes it
        }
        for (const rect of Array.from(range.getClientRects())) {
          drawSquiggle(hostRect, scale, rect);
        }
      }
    };

    // Re-check spelling across the painted paragraphs, then draw. Runs on
    // content / zoom changes (debounced), never on plain scroll.
    const rescan = () => {
      if (disposed || !checker) return;
      try {
        const found: MisspelledWord[] = [];
        found.push(...collectMisspellings(container, (w) => checker!.isCorrect(w)));
        words = found;
      } catch (err) {
        console.error("Spell-check overlay failed:", err);
      }
      redraw();
    };

    const schedule = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(rescan, 300);
    };

    // ── click-to-fix ─────────────────────────────────────────────────────────
    const applyFix = (target: MisspelledWord, replacement: string) => {
      const view = viewRef.current;
      if (!view || Number.isNaN(target.from)) return;
      // Drift guard: the offset math assumes plain-text paragraphs, so it can be
      // wrong when a paragraph contains inline atoms or is split across a page
      // break. Only replace if the computed PM range actually holds the flagged
      // word — otherwise no-op rather than silently overwriting the wrong text.
      if (view.state.doc.textBetween(target.from, target.to) !== target.word) return;
      try {
        view.dispatch(view.state.tr.insertText(replacement, target.from, target.to));
        // The doc changed programmatically — tell the host so autosave arms
        // (the editor's own onChange doesn't reliably fire for our dispatch).
        onEditRef.current?.();
      } catch (err) {
        console.error("Spell-check fix failed:", err);
      }
    };

    // Capture-phase click (not mousedown): fires AFTER the editor has placed the
    // caret, so we never interfere with normal cursor placement or selection —
    // we only add the suggestions popover on top.
    const onClick = (e: MouseEvent) => {
      if (!checker || words.length === 0) return;
      const hit = caretNodeOffsetAtPoint(e.clientX, e.clientY);
      const match = matchWordAt(words, hit?.node ?? null, hit?.offset ?? -1);
      if (!match) return;
      const range = document.createRange();
      try {
        range.setStart(match.node, match.start);
        range.setEnd(match.node, match.end);
      } catch {
        return;
      }
      showPopover(range.getBoundingClientRect(), checker.suggest(match.word), {
        onPick: (s) => applyFix(match, s),
        onIgnore: () => {
          checker?.ignore(match.word);
          rescan();
        },
      });
    };
    container.addEventListener("click", onClick, true);

    // No scroll handler: the squiggle layer lives inside the fork's scroll
    // container and is positioned in its content space, so the browser scrolls
    // the squiggles together with the text natively (no JS, no lag). We only
    // re-scan on content / zoom changes, which the MutationObserver catches.

    getSpellChecker().then((c) => {
      if (disposed) return;
      checker = c;
      if (!c) return;
      observer = new MutationObserver((records) => {
        // Ignore our own squiggle-layer writes — they're inside `container`, so
        // without this every redraw would retrigger a rescan (300ms loop).
        if (mutationsConfinedTo(records, layer)) return;
        schedule();
      });
      observer.observe(container, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: ["style"],
      });
      schedule();
    });

    return () => {
      disposed = true;
      window.clearTimeout(timer);
      observer?.disconnect();
      container.removeEventListener("click", onClick, true);
      layer.remove();
      // Revert the benign position nudge if we made it.
      if (host && setHostRelative) host.style.position = "";
      closePopover();
    };
  }, [containerRef, viewRef]);
}

// ── suggestion popover (vanilla DOM) ─────────────────────────────────────────
let popoverEl: HTMLElement | null = null;
let onDocDown: ((e: MouseEvent) => void) | null = null;
let onKeyDown: ((e: KeyboardEvent) => void) | null = null;

function closePopover() {
  popoverEl?.remove();
  popoverEl = null;
  if (onDocDown) {
    document.removeEventListener("mousedown", onDocDown, true);
    onDocDown = null;
  }
  if (onKeyDown) {
    document.removeEventListener("keydown", onKeyDown, true);
    onKeyDown = null;
  }
}

function showPopover(
  anchor: DOMRect,
  suggestions: string[],
  handlers: { onPick: (s: string) => void; onIgnore: () => void }
) {
  closePopover();
  const el = document.createElement("div");
  el.setAttribute("role", "menu");
  el.setAttribute("aria-label", "Spelling suggestions");
  el.setAttribute(
    "style",
    [
      "position:fixed",
      // Provisional position; clamped to the viewport after measuring (below).
      `left:${anchor.left}px`,
      `top:${anchor.bottom + 4}px`,
      "z-index:1000",
      "min-width:168px",
      "max-width:260px",
      "padding:4px",
      "background:#fff",
      "border:1px solid #e2e5eb",
      "border-radius:8px",
      "box-shadow:0 4px 16px rgba(26,25,57,0.14)",
      "font-size:13px",
    ].join(";")
  );

  const itemStyle =
    "display:block;width:100%;padding:6px 10px;border:none;border-radius:6px;background:transparent;text-align:left;font-size:13px;cursor:pointer;";

  if (suggestions.length === 0) {
    const none = document.createElement("div");
    none.setAttribute("style", "padding:6px 10px;color:#8a8a98;");
    none.textContent = "No suggestions";
    el.appendChild(none);
  } else {
    for (const s of suggestions) {
      const b = document.createElement("button");
      b.type = "button";
      b.setAttribute("role", "menuitem");
      b.setAttribute("style", `${itemStyle}font-weight:600;color:#1a1939;`);
      b.textContent = s;
      b.addEventListener("mouseenter", () => (b.style.background = "#f0f7ff"));
      b.addEventListener("mouseleave", () => (b.style.background = "transparent"));
      // mousedown only guards against the editor stealing focus / placing a
      // caret; the action fires on click so keyboard (Enter/Space) works too.
      b.addEventListener("mousedown", (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
      });
      b.addEventListener("click", (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        handlers.onPick(s);
        closePopover();
      });
      el.appendChild(b);
    }
  }

  const ignore = document.createElement("button");
  ignore.type = "button";
  ignore.setAttribute("role", "menuitem");
  ignore.setAttribute(
    "style",
    `${itemStyle}margin-top:4px;border-top:1px solid #eef1f6;color:#8a8a98;`
  );
  ignore.textContent = "Ignore";
  ignore.addEventListener("mousedown", (ev) => {
    ev.preventDefault();
    ev.stopPropagation();
  });
  ignore.addEventListener("click", (ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    handlers.onIgnore();
    closePopover();
  });
  el.appendChild(ignore);

  document.body.appendChild(el);
  popoverEl = el;

  // Clamp within the viewport now that the popover has a measurable size, so it
  // never opens off-screen for a squiggle near the right/bottom edge.
  const margin = 8;
  const box = el.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let left = anchor.left;
  let top = anchor.bottom + 4;
  if (left + box.width > vw - margin)
    left = Math.max(margin, vw - margin - box.width);
  if (top + box.height > vh - margin) {
    // Not enough room below the word — flip above it if that fits better.
    const above = anchor.top - 4 - box.height;
    top = above >= margin ? above : Math.max(margin, vh - margin - box.height);
  }
  el.style.left = `${left}px`;
  el.style.top = `${top}px`;

  // Move keyboard focus into the menu and support arrow / Escape navigation.
  const items = Array.from(el.querySelectorAll<HTMLElement>('[role="menuitem"]'));
  items[0]?.focus();
  onKeyDown = (e: KeyboardEvent) => {
    if (!popoverEl) return;
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      closePopover();
      return;
    }
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (items.length === 0) return;
      const active = document.activeElement as HTMLElement | null;
      const idx = active ? items.indexOf(active) : -1;
      const dir = e.key === "ArrowDown" ? 1 : -1;
      const next = (idx + dir + items.length) % items.length;
      items[next]?.focus();
    }
  };
  document.addEventListener("keydown", onKeyDown, true);

  onDocDown = (e: MouseEvent) => {
    if (popoverEl && !popoverEl.contains(e.target as Node)) closePopover();
  };
  window.setTimeout(
    () => onDocDown && document.addEventListener("mousedown", onDocDown, true),
    0
  );
}
